import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Eye, EyeOff } from "lucide-react";
import { API_URL } from "../config";

const Auth = () => {
  const [tab, setTab] = useState<"login" | "register" | "forgot" | "reset">(
    "login",
  );
  const [resetToken, setResetToken] = useState<string | null>(null);

  // login
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginStatus, setLoginStatus] = useState<"pending" | "rejected" | null>(
    null,
  );
  const [showLoginPassword, setShowLoginPassword] = useState(false); // ADAUGĂ PENTRU VIZIBILITATE

  // register
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirmPassword, setRegConfirmPassword] = useState("");
  const [regError, setRegError] = useState("");
  const [regSuccess, setRegSuccess] = useState("");
  const [regPhone, setRegPhone] = useState("+44");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const formatUKPhone = (value: string) => {
    let digits = value.replace(/\D/g, "");
    if (!digits.startsWith("44")) digits = "44" + digits;
    let formatted = "+" + digits.slice(0, 2);
    if (digits.length > 2) formatted += " " + digits.slice(2, 6);
    if (digits.length > 6) formatted += " " + digits.slice(6, 11);
    return formatted.trim();
  };

  const [showRegPassword, setShowRegPassword] = useState(false); // ADAUGĂ
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false); // ADAUGĂ

  // forgot password
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");

  // reset password
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false); // ADAUGĂ
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false); // ADAUGĂ

  const navigate = useNavigate();

  // Verifică dacă există token în URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    if (token) {
      setResetToken(token);
      setTab("reset");
      // Elimină token-ul din URL pentru securitate
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    setLoginStatus(null);

    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });

      const data = await res.json();
      if (!res.ok) {
        if (data.status === "pending") {
          setLoginStatus("pending");
        } else if (data.status === "rejected") {
          setLoginStatus("rejected");
        } else {
          setLoginError(data.message || "Login failed");
        }
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("role", data.role);

      if (data.role === "admin") {
        navigate("/admin-verify");
      } else {
        navigate("/my-orders");
      }
    } catch {
      setLoginError("Network error");
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError("");
    setRegSuccess("");

    if (!privacyAccepted) {
      setRegError(
        "Trebuie să accepți politica de confidențialitate pentru a continua.",
      );
      return;
    }

    if (regPassword !== regConfirmPassword) {
      setRegError("Passwords do not match");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: regName,
          email: regEmail,
          phone: regPhone,
          password: regPassword,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setRegError(data.message || "Registration failed");
        return;
      }

      setRegSuccess(
        "Înregistrare trimisă! Așteptați aprobarea administratorului.",
      );
      setRegName("");
      setRegEmail("");
      setRegPassword("");
      setRegConfirmPassword("");
    } catch {
      setRegError("Network error");
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError("");
    setForgotMessage("");

    try {
      const res = await fetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });

      const data = await res.json();
      setForgotMessage(data.message);
      setForgotEmail("");
    } catch {
      setForgotError("Network error");
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError("");
    setResetSuccess("");

    if (newPassword !== confirmPassword) {
      setResetError("Parolele nu se potrivesc");
      return;
    }

    if (newPassword.length < 6) {
      setResetError("Parola trebuie să aibă cel puțin 6 caractere");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: resetToken,
          password: newPassword,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setResetError(data.message || "Reset failed");
        return;
      }

      setResetSuccess(
        "Parola a fost actualizată cu succes! Poți să te conectezi acum.",
      );
      setNewPassword("");
      setConfirmPassword("");

      // ADAUGĂ - DUPĂ 3 SECUNDE REDIRECT LA LOGIN ȘI ELIMINĂ TAB-UL RESET
      setTimeout(() => {
        setResetToken(null); // Elimină token-ul
        setTab("login"); // Schimbă la login
        setResetSuccess(""); // Curăță mesajul de succes
      }, 3000);
    } catch {
      setResetError("Network error");
    }
  };
  return (
    <Layout>
      <div className="flex justify-center items-center py-12 px-4">
        <div className="w-full max-w-md bg-white dark:bg-neutral-600 shadow-lg rounded-2xl p-6">
          {/* Tabs - DOAR LOGIN ȘI REGISTER */}
          <div className="flex justify-center mb-6 space-x-6">
            <button
              onClick={() => setTab("login")}
              className={`pb-2 border-b-2 transition ${
                tab === "login"
                  ? "border-blue-500 text-blue-800 font-semibold"
                  : "border-transparent hover:text-blue-600"
              }`}
            >
              Login
            </button>
            <button
              onClick={() => setTab("register")}
              className={`pb-2 border-b-2 transition ${
                tab === "register"
                  ? "border-green-600 text-green dark:border-green-200 dark:text-green-200 font-semibold"
                  : "border-transparent hover:text-green-600 dark:hover:text-green-200"
              }`}
            >
              Register
            </button>
            {/* DOAR AFIȘEAZĂ RESET PASSWORD DACĂ EXISTĂ TOKEN */}
            {resetToken && (
              <button
                onClick={() => setTab("reset")}
                className={`pb-2 border-b-2 transition ${
                  tab === "reset"
                    ? "border-purple-500 text-purple-700 font-semibold"
                    : "border-transparent hover:text-purple-600"
                }`}
              >
                Reset Password
              </button>
            )}
          </div>

          {/* Login Form CU OCHIȘOR */}
          {tab === "login" && (
            <form onSubmit={handleLogin} className="space-y-4">
              {loginError && <p className="text-red-600">{loginError}</p>}

              {/* Status Messages pentru pending/rejected */}
              {loginStatus === "pending" && (
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                  <p className="font-bold">Account Pending Approval</p>
                  <p className="text-sm">
                    Your account is currently pending administrator approval.
                    You will receive an email once your account has been
                    approved.
                  </p>
                </div>
              )}

              {loginStatus === "rejected" && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  <p className="font-bold">Cont respins</p>
                  <p className="text-sm">
                    Din păcate, contul tău a fost respins. Te rugăm să
                    contactezi suportul pentru mai multe informații.
                  </p>
                </div>
              )}

              <input
                type="email"
                placeholder="Email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
                className="w-full p-3 border rounded-lg bg-gray-50 dark:bg-neutral-600"
              />

              {/* PASSWORD CU OCHIȘOR */}
              <div className="relative">
                <input
                  type={showLoginPassword ? "text" : "password"}
                  placeholder="Password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  className="w-full p-3 border rounded-lg bg-gray-50 dark:bg-neutral-600 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowLoginPassword(!showLoginPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                >
                  {showLoginPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-400 hover:bg-blue-700 text-white py-2 rounded-lg transition"
              >
                Login
              </button>

              {/* LINK-UL PENTRU FORGOT PASSWORD */}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => setTab("forgot")}
                  className="text-blue-500 hover:text-blue-700 text-sm transition"
                >
                  Forgot password
                </button>
              </div>
            </form>
          )}

          {/* Register Form CU OCHIȘOR */}
          {tab === "register" && (
            <form onSubmit={handleRegister} className="space-y-4">
              {regError && <p className="text-red-600">{regError}</p>}
              {regSuccess && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                  <p className="font-bold">Registration Submitted!</p>
                  <p className="text-sm">
                    Please wait for administrator approval. You will receive an
                    email once your account has been approved.
                  </p>
                </div>
              )}

              {/* Nume */}
              <input
                type="text"
                placeholder="Name"
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                required
                className="w-full p-3 border rounded-lg bg-gray-100 dark:bg-neutral-600"
              />

              {/* Email */}
              <input
                type="email"
                placeholder="Email"
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                required
                className="w-full p-3 border rounded-lg bg-gray-100 dark:bg-neutral-600"
              />

              {/* Telefon UK */}
              <input
                type="tel"
                placeholder="+44 1234 567890"
                value={regPhone}
                onChange={(e) => setRegPhone(formatUKPhone(e.target.value))}
                required
                className="w-full p-3 border rounded-lg bg-gray-100 dark:bg-neutral-600 tracking-wider"
              />

              {/* Parola */}
              <div className="relative">
                <input
                  type={showRegPassword ? "text" : "password"}
                  placeholder="Password"
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  required
                  className="w-full p-3 border rounded-lg bg-gray-100 dark:bg-neutral-600 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowRegPassword(!showRegPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                >
                  {showRegPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* Confirmare parolă */}
              <div className="relative">
                <input
                  type={showRegConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={regConfirmPassword}
                  onChange={(e) => setRegConfirmPassword(e.target.value)}
                  required
                  className="w-full p-3 border rounded-lg bg-gray-100 dark:bg-neutral-600 pr-12"
                />
                <button
                  type="button"
                  onClick={() =>
                    setShowRegConfirmPassword(!showRegConfirmPassword)
                  }
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                >
                  {showRegConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              <div className="flex items-start space-x-2 mt-2">
                <input
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  required
                  className="mt-1"
                />
                <span className="text-sm">
                  I agree to the{" "}
                  <button
                    type="button"
                    onClick={() => setShowPrivacy(true)}
                    className="text-blue-600 underline"
                  >
                    Privacy Policy
                  </button>
                </span>
              </div>

              <button
                type="submit"
                className="w-full bg-green-700 hover:bg-green text-white py-2 rounded-lg transition"
              >
                Register
              </button>
            </form>
          )}

          {/* Forgot Password Form - APARE DOAR CÂND SE APASĂ PE LINK */}
          {tab === "forgot" && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold">Resetare Parolă</h3>
                <p className="text-sm text-gray-600">
                  Introdu email-ul pentru a primi link de resetare
                </p>
              </div>

              {forgotError && <p className="text-red-600">{forgotError}</p>}
              {forgotMessage && (
                <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
                  <p className="text-sm">{forgotMessage}</p>
                </div>
              )}

              <input
                type="email"
                placeholder="Email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                className="w-full p-3 border rounded-lg bg-gray-50 dark:bg-neutral-600"
              />
              <button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-700 text-white py-2 rounded-lg transition"
              >
                Trimite Link Resetare
              </button>

              {/* LINK PENTRU A REVENI LA LOGIN */}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => setTab("login")}
                  className="text-blue-500 hover:text-blue-700 text-sm transition"
                >
                  ← Înapoi la Login
                </button>
              </div>
            </form>
          )}

          {/* Reset Password Form CU OCHIȘOR */}
          {tab === "reset" && resetToken && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold">Parolă Nouă</h3>
                <p className="text-sm text-gray-600">Introdu parola nouă</p>
              </div>

              {resetError && <p className="text-red-600">{resetError}</p>}
              {resetSuccess && (
                <div className="bg-green-100 border border-green-400 text-green-200 px-4 py-3 rounded">
                  <p className="text-sm font-bold">Succes!</p>
                  <p className="text-sm">{resetSuccess}</p>
                </div>
              )}

              {/* NEW PASSWORD CU OCHIȘOR */}
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Parola nouă"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="w-full p-3 border rounded-lg bg-gray-50 dark:bg-neutral-600 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                >
                  {showNewPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* CONFIRM NEW PASSWORD CU OCHIȘOR */}
              <div className="relative">
                <input
                  type={showConfirmNewPassword ? "text" : "password"}
                  placeholder="Confirmă parola nouă"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full p-3 border rounded-lg bg-gray-50 dark:bg-neutral-600 pr-12"
                />
                <button
                  type="button"
                  onClick={() =>
                    setShowConfirmNewPassword(!showConfirmNewPassword)
                  }
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                >
                  {showConfirmNewPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              <button
                type="submit"
                className="w-full bg-purple-500 hover:bg-purple-700 text-white py-2 rounded-lg transition"
              >
                Actualizează Parola
              </button>

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => setTab("login")}
                  className="text-blue-500 hover:text-blue-700 text-sm transition"
                >
                  ← Înapoi la Login
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {showPrivacy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-600 rounded-2xl shadow-xl p-6 max-w-3xl w-full relative">
            <button
              onClick={() => setShowPrivacy(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
            >
              ✕
            </button>
            <h2 className="text-2xl font-bold mb-4 text-center">
              Privacy Policy
            </h2>
            <div className="max-h-[70vh] overflow-y-auto text-sm space-y-4 leading-relaxed">
              <p>
                This Privacy Policy explains how we collect, use, and protect
                your personal information when you create an account, place
                orders, or interact with our platform. By registering, you agree
                to the terms below.
              </p>

              <h3 className="font-semibold text-lg mt-4">1. Data We Collect</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  Full name, email address, and phone number (for account
                  creation and contact).
                </li>
                <li>Login credentials (securely hashed password).</li>
                <li>Order history and preferences.</li>
                <li>
                  Technical information such as IP address, browser type, and
                  device data.
                </li>
              </ul>

              <h3 className="font-semibold text-lg mt-4">
                2. How We Use Your Data
              </h3>
              <ul className="list-disc list-inside space-y-1">
                <li>To create and manage your account.</li>
                <li>
                  To communicate order updates, confirmations, and security
                  alerts.
                </li>
                <li>To improve our services and ensure platform security.</li>
                <li>
                  To comply with legal obligations (e.g., tax and accounting
                  regulations).
                </li>
              </ul>

              <h3 className="font-semibold text-lg mt-4">3. Data Sharing</h3>
              <p>
                We do not sell or rent your personal data. Your information may
                be shared only with:
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  Authorized employees or service providers strictly for order
                  processing.
                </li>
                <li>Legal authorities if required by law or court order.</li>
              </ul>

              <h3 className="font-semibold text-lg mt-4">4. Data Retention</h3>
              <p>
                Your data is stored securely and retained only as long as
                necessary to fulfill our contractual and legal obligations. You
                may request account deletion at any time.
              </p>

              <h3 className="font-semibold text-lg mt-4">5. Your Rights</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Right to access, correct, or delete your personal data.</li>
                <li>Right to restrict or object to data processing.</li>
                <li>
                  Right to data portability (receive a copy of your data in
                  electronic format).
                </li>
                <li>
                  Right to file a complaint with a Data Protection Authority.
                </li>
              </ul>

              <h3 className="font-semibold text-lg mt-4">6. Security</h3>
              <p>
                We use encryption, secure storage, and access controls to
                protect your data. However, no method of transmission over the
                Internet is 100% secure.
              </p>

              <h3 className="font-semibold text-lg mt-4">7. Contact Us</h3>
              <p>
                For questions regarding this Privacy Policy or to exercise your
                rights, please contact us at:{" "}
                <strong>support@yourdomain.com</strong>.
              </p>

              <p className="text-xs text-gray-500 mt-6 text-center">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Auth;
